import type { Theme } from 'hephaestus/core';
import { Box, Skeleton, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { BORDERED_BOX_SX, SECTION_HEADER_SX } from '../styles';
import type { PortfolioReport, SubTradeRequest } from '../types';
import { PortfolioRowDetailPendingTradesSummary } from './portfolioRowDetailPendingTradesSummary';
import { PortfolioRowDetailPendingTradesTable } from './portfolioRowDetailPendingTradesTable';
export type PortfolioRowDetailPendingTradesProps = {
  pendingTrades: SubTradeRequest[];
  pendingTradesLoading: boolean;
  pendingTradesCalled: boolean;
  report: PortfolioReport;
  theme: Theme;
};

export const PortfolioRowDetailPendingTrades = ({
  pendingTrades,
  pendingTradesLoading,
  pendingTradesCalled,
  report,
  theme,
}: PortfolioRowDetailPendingTradesProps) => {
  const { t } = useTranslation('portfolioTradingHub');

  const isSettled = pendingTradesCalled && !pendingTradesLoading;
  const reportPendingCount = report.pendingSubTradeRequests ?? 0;
  // Match generalInfo stale semantics: warn only when the report overstates.
  // Live-only trades already surface via titleWithCount + the table.
  const hasStaleCount = isSettled && reportPendingCount > pendingTrades.length;

  return (
    <Box
      sx={{
        mb: 2,
      }}
    >
      <Typography
        variant='labelSmall'
        component='div'
        sx={{ mb: 1, ...SECTION_HEADER_SX }}
      >
        {isSettled
          ? t('rowDetail.pendingTrades.titleWithCount', {
              count: pendingTrades.length,
            })
          : t('rowDetail.pendingTrades.title')}
      </Typography>
      {hasStaleCount && (
        <Typography
          variant='bodySmall'
          component='div'
          sx={{ mb: 1, color: theme.color.warning }}
        >
          {t('rowDetail.pendingTrades.staleCountWarning', {
            count: reportPendingCount,
          })}
        </Typography>
      )}
      <Box
        sx={{
          ...BORDERED_BOX_SX,
          bgcolor: theme.color.surface,
          overflow: 'hidden',
        }}
      >
        {pendingTradesLoading || !pendingTradesCalled ? (
          <Box
            sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} width='100%' height={28} />
            ))}
          </Box>
        ) : pendingTrades.length === 0 ? (
          <Box sx={{ p: 1.5 }}>
            <Typography
              variant='bodySmall'
              sx={{ color: theme.color.onSurfaceVariant }}
            >
              {t('rowDetail.pendingTrades.noActiveTrades')}
            </Typography>
          </Box>
        ) : (
          <>
            <PortfolioRowDetailPendingTradesTable
              pendingTrades={pendingTrades}
              theme={theme}
            />
            <PortfolioRowDetailPendingTradesSummary
              pendingTrades={pendingTrades}
              theme={theme}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
